import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Upload, Trash2, KeyRound } from "lucide-react";

import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { Spinner } from "../components/common/Spinner";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { setAppName, uploadLogo, deleteLogo, logoUrl } from "../api/branding";
import {
  getViewCredentialsState, setViewCredentialsPassword, clearViewCredentialsPassword,
} from "../api/settings";
import type { ViewCredentialsState } from "../api/settings";
import { useBrandingStore } from "../store/brandingStore";

const ACCEPT = ".png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp";
const MAX_BYTES = 2 * 1024 * 1024;

export default function SettingsPage() {
  const branding = useBrandingStore();
  const setBranding = useBrandingStore((s) => s.set);
  const load = useBrandingStore((s) => s.load);

  const [name, setName] = useState(branding.app_name);
  const [savingName, setSavingName] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [viewPw, setViewPw] = useState<ViewCredentialsState | null>(null);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [confirmClearPw, setConfirmClearPw] = useState(false);

  useEffect(() => { setName(branding.app_name); }, [branding.app_name]);
  useEffect(() => { getViewCredentialsState().then(setViewPw).catch(() => setViewPw(null)); }, []);

  if (!branding.loaded) return <div className="p-6"><Spinner /></div>;

  const saveName = async () => {
    const trimmed = name.trim();
    if (trimmed === branding.app_name) { toast("Sin cambios"); return; }
    setSavingName(true);
    try {
      const next = await setAppName(trimmed);
      setBranding(next);
      toast.success(trimmed ? "Nombre actualizado" : "Nombre eliminado (solo logo)");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error guardando nombre");
    } finally {
      setSavingName(false);
    }
  };

  const onPickFile = async (f: File) => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(`Archivo muy grande (${Math.round(f.size / 1024)} KB). Máximo ${MAX_BYTES / 1024} KB.`);
      return;
    }
    setUploading(true);
    const t = toast.loading(`Subiendo ${f.name}...`);
    try {
      const next = await uploadLogo(f);
      setBranding(next);
      toast.success("Logo actualizado", { id: t });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error subiendo logo", { id: t });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doDeleteLogo = async () => {
    setConfirmDel(false);
    const t = toast.loading("Eliminando logo...");
    try {
      const next = await deleteLogo();
      setBranding(next);
      toast.success("Logo eliminado, se restauró el nombre por defecto", { id: t });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error eliminando", { id: t });
    }
  };

  const savePw = async () => {
    if (pw1.length < 4) { toast.error("La contraseña debe tener al menos 4 caracteres"); return; }
    if (pw1.length > 64) { toast.error("La contraseña no puede exceder 64 caracteres"); return; }
    if (pw1 !== pw2) { toast.error("Las contraseñas no coinciden"); return; }
    setSavingPw(true);
    try {
      const next = await setViewCredentialsPassword(pw1);
      setViewPw(next);
      setPw1(""); setPw2("");
      toast.success("Contraseña de \"Ver datos\" actualizada");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error guardando la contraseña");
    } finally {
      setSavingPw(false);
    }
  };

  const doClearPw = async () => {
    setConfirmClearPw(false);
    try {
      const next = await clearViewCredentialsPassword();
      setViewPw(next);
      setPw1(""); setPw2("");
      toast.success("Contraseña de \"Ver datos\" eliminada");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error eliminando la contraseña");
    }
  };

  return (
    <div className="p-4 max-w-3xl space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Configuración general</h1>
        <p className="text-xs text-muted mt-0.5">
          Personalización visual de la aplicación. Estos cambios se ven en el login y en la barra superior.
        </p>
      </div>

      <section className="border border-line rounded p-4 bg-panel/30 space-y-3">
        <div>
          <h2 className="text-sm font-semibold">Nombre de la aplicación</h2>
          <p className="text-xs text-muted mt-0.5">
            Opcional. Se muestra junto al logo y como título de la pestaña del navegador.
            Dejalo vacío si solo querés mostrar el logo.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Input
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            placeholder="Mi Empresa"
            className="flex-1"
          />
          <Button variant="primary" onClick={saveName} loading={savingName}>
            Guardar
          </Button>
          <Button variant="ghost" onClick={() => setName(branding.app_name)} disabled={savingName}>
            Restablecer
          </Button>
        </div>
      </section>

      <section className="border border-line rounded p-4 bg-panel/30 space-y-3">
        <div>
          <h2 className="text-sm font-semibold">Logo</h2>
          <p className="text-xs text-muted mt-0.5">
            PNG, JPG, SVG o WEBP. Máximo {MAX_BYTES / 1024} KB. Se muestra en la barra superior y en la página de login.
          </p>
        </div>

        <div className="flex items-start gap-6 flex-wrap">
          <div className="flex flex-col items-center gap-2">
            <div className="border border-line rounded p-4 bg-bg w-48 h-32 flex items-center justify-center">
              {branding.has_logo ? (
                <img
                  src={logoUrl(branding.updated_at)}
                  alt="Logo actual"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <span className="text-muted text-xs italic">Sin logo (se muestra el nombre)</span>
              )}
            </div>
            <span className="text-xs text-muted">Vista previa</span>
          </div>

          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickFile(f); }}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={() => fileRef.current?.click()}
              loading={uploading}
            >
              <Upload size={14} /> {branding.has_logo ? "Reemplazar logo" : "Subir logo"}
            </Button>
            {branding.has_logo && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDel(true)}
                disabled={uploading}
              >
                <Trash2 size={14} /> Eliminar logo
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => load()}
              disabled={uploading}
            >
              Recargar desde el servidor
            </Button>
          </div>
        </div>
      </section>

      <section className="border border-line rounded p-4 bg-panel/30 space-y-3">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <KeyRound size={14} /> Contraseña para "Ver datos" de dispositivos
          </h2>
          <p className="text-xs text-muted mt-0.5">
            Protege el botón "Ver datos" de la página Dispositivos, que muestra el
            usuario, la contraseña y la IP de cada equipo. Quien gestione dispositivos
            deberá ingresar esta contraseña cada vez para verlos.
          </p>
        </div>

        <div className="text-xs">
          Estado:{" "}
          {viewPw?.configured ? (
            <span className="text-green-500 font-medium">Configurada</span>
          ) : (
            <>
              <span className="text-amber-500 font-medium">No configurada</span>
              <span className="text-muted"> — el botón "Ver datos" no funcionará hasta definirla.</span>
            </>
          )}
        </div>

        <div className="flex items-end gap-2 flex-wrap">
          <Input
            label={viewPw?.configured ? "Nueva contraseña" : "Contraseña"}
            type="password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            maxLength={64}
            autoComplete="new-password"
            placeholder="Mínimo 4 caracteres"
            className="w-56"
          />
          <Input
            label="Confirmar contraseña"
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            maxLength={64}
            autoComplete="new-password"
            className="w-56"
          />
          <Button variant="primary" onClick={savePw} loading={savingPw} disabled={!pw1 || !pw2}>
            {viewPw?.configured ? "Cambiar" : "Guardar"}
          </Button>
          {viewPw?.configured && (
            <Button variant="ghost" onClick={() => setConfirmClearPw(true)} disabled={savingPw}>
              <Trash2 size={14} /> Eliminar
            </Button>
          )}
        </div>
      </section>

      <ConfirmDialog
        open={confirmDel}
        title="Eliminar logo"
        message="Esto borra el logo del servidor. La aplicación volverá a mostrar solo el nombre. ¿Continuar?"
        destructive
        confirmText="Eliminar"
        onConfirm={doDeleteLogo}
        onCancel={() => setConfirmDel(false)}
      />

      <ConfirmDialog
        open={confirmClearPw}
        title="Eliminar contraseña de 'Ver datos'"
        message="Sin esta contraseña, el botón 'Ver datos' de la página Dispositivos dejará de funcionar hasta que definas una nueva. ¿Continuar?"
        destructive
        confirmText="Eliminar"
        onConfirm={doClearPw}
        onCancel={() => setConfirmClearPw(false)}
      />
    </div>
  );
}
